import {createTheme} from '@material-ui/core/styles'
import {darken, lighten} from 'polished'

const fontFam = '"Roboto", "Arial", sans-serif'

// Palette
export const colorPrimary = '#017eff'
export const colorSecondary = '#eb5757'
export const colorBg = '#f6f6f9'
export const colorInfo = '#1070CA'
export const colorSuccess = '#219653'
export const colorWarning = '#feb52c'
export const colorDanger = '#eb5757'

export const colorBlack = '#000'
export const colorWhite = '#fff'

export const grey50 = '#fafafa'
export const grey100 = '#f5f5f5'
export const grey200 = '#eeeeee'
export const grey300 = '#e0e0e0'
export const grey400 = '#bdbdbd'
export const grey500 = '#9e9e9e'
export const grey600 = '#757575'
export const grey700 = '#616161'
export const grey800 = '#424242'
export const grey900 = '#212121'

export const borderColor = '#E0E0E0'

export const defaultPrimary = '#37474F'
export const defaultSecondary = 'rgba(0, 0, 0, 0.54)'
export const defaultDisabled = 'rgba(0, 0, 0, 0.38)'
export const defaultHint = 'rgba(0, 0, 0, 0.38)'

const valueLighten = 0.2
const valueDarken = 0.12

// Type
const h1 = '3.75em'
const h2 = '3em'
const h3 = '2.25rem'
const h4 = '1.75em'
const h5 = '1.3125rem'
const h6 = '1.125rem'

const subtitle1 = '1rem'
const subtitle2 = '0.875rem'
const body1 = '1rem'
const body2 = '0.875rem'
const button = '0.875rem'
const caption = '0.6875rem'
const overline = '0.6125rem'

const hMargin = 0.55
const hTopMar = hMargin + 'em'
const hBotMar = hMargin / 2 + 'em'

// Forms
const formType = 'standard'

export default createTheme({
  props: {
    MuiButtonBase: {
      disableRipple: true,
    },
    MuiTextField: {
      variant: formType,
    },
    Input: {
      variant: formType,
    },
    MuiIcon: {
      fontSize: 'small',
    },
  },
  shape: 8,
  // spacing: 4,
  // Color
  palette: {
    type: 'light',
    common: {
      black: colorBlack,
      white: colorWhite,
    },
    primary: {
      contrastText: colorWhite,
      main: colorPrimary,
      light: lighten(valueLighten, colorPrimary),
      dark: darken(valueDarken, colorPrimary),
    },
    secondary: {
      contrastText: colorWhite,
      main: colorSecondary,
      light: lighten(valueLighten, colorSecondary),
      dark: darken(valueDarken * 0.8, colorSecondary),
    },
    divider: 'rgba(0, 0, 0, 0.12)',
    background: {
      default: colorBg,
    },
    action: {
      active: defaultSecondary,
      hover: 'rgba(0, 0, 0, 0.08)',
      hoverOpacity: 0.08,
      selected: 'rgba(0, 0, 0, 0.14)',
      disabled: 'rgba(0, 0, 0, 0.26)',
      disabledBackground: 'rgba(0, 0, 0, 0.12)',
    },
    success: {
      contrastText: colorWhite,
      main: colorSuccess,
      light: lighten(valueLighten, colorSuccess),
      dark: darken(valueDarken, colorSuccess),
    },
    info: {
      contrastText: colorWhite,
      main: colorInfo,
      light: lighten(valueLighten, colorInfo),
      dark: darken(valueDarken, colorInfo),
    },
    warning: {
      contrastText: colorWhite,
      main: colorWarning,
      light: lighten(valueLighten, colorWarning),
      dark: darken(valueDarken, colorWarning),
    },
    danger: {
      contrastText: colorWhite,
      main: colorDanger,
      light: lighten(valueLighten, colorDanger),
      dark: darken(valueDarken, colorDanger),
    },
    border: borderColor,
    grey: {
      '50': grey50,
      '100': grey100, // Default Color
      '200': grey200,
      '300': grey300,
      '400': grey400,
      '500': grey500,
      '600': grey600,
      '700': grey700,
      '800': grey800,
      '900': grey900,
      A100: '#d5d5d5',
      A200: '#aaaaaa',
      A400: '#303030',
      A700: '#616161',
    },
    surface: {
      main: '#f6f6f9',
      light: '#fff',
      dark: '#292d32',
      lightBlue: '#e8f3ff33',
    },
    text: {
      primary: defaultPrimary,
      secondary: defaultSecondary,
      disabled: defaultDisabled,
      hint: defaultHint,
    },
    contrastThreshold: 3,
    tonalOffset: 0.2,
  },
  // Typography
  typography: {
    fontFamily: fontFam,
    fontSize: 14,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    h1: {
      fontSize: h1,
      fontWeight: 200,
      fontFamily: fontFam,
      letterSpacing: '-.02em',
      lineHeight: '1.14286em',
      marginLeft: '-.04em',
      marginTop: hTopMar,
      marginBottom: hBotMar,
    },
    h2: {
      fontSize: h2,
      fontWeight: 400,
      fontFamily: fontFam,
      letterSpacing: '-.02em',
      lineHeight: '1.30357em',
      marginLeft: '-.02em',
      marginTop: hTopMar,
      marginBottom: hBotMar,
    },
    h3: {
      fontSize: h3,
      fontWeight: 400,
      fontFamily: fontFam,
      lineHeight: '1.13333em',
      marginLeft: '-.02em',
      marginTop: hTopMar,
      marginBottom: hBotMar,
    },
    h4: {
      fontSize: h4,
      fontWeight: 500,
      fontFamily: fontFam,
      lineHeight: '1.20588em',
      marginTop: hTopMar,
      marginBottom: hBotMar,
    },
    h5: {
      fontSize: h5,
      fontWeight: 700,
      fontFamily: fontFam,
      lineHeight: '1.35417em',
      marginTop: hTopMar,
      marginBottom: hBotMar,
      textTransform: 'uppercase',
    },
    h6: {
      fontSize: h6,
      fontWeight: 500,
      fontFamily: fontFam,
      lineHeight: '1.16667em',
      marginTop: hTopMar,
      marginBottom: hBotMar,
    },
    subtitle1: {
      fontSize: subtitle1,
      fontWeight: 400,
      fontFamily: fontFam,
      lineHeight: '1.5em',
      marginTop: hTopMar,
      marginBottom: hBotMar,
    },
    subtitle2: {
      color: colorWhite,
      fontFamily: fontFam,
      fontWeight: 700,
      fontSize: subtitle2,
      lineHeight: 1.57,
      letterSpacing: '0.00714em',
      textTransform: 'uppercase',
      marginTop: hTopMar,
      marginBottom: hBotMar,
    },
    body1: {
      fontFamily: fontFam,
      fontWeight: 400,
      fontSize: body1,
      lineHeight: 1.5,
      letterSpacing: '0.00938em',
    },
    body2: {
      fontFamily: fontFam,
      fontWeight: 400,
      fontSize: body2,
      lineHeight: 1.5,
      letterSpacing: '0.01071em',
    },
    button: {
      fontFamily: fontFam,
      fontWeight: 500,
      fontSize: button,
      lineHeight: 1.5,
      letterSpacing: '0.02857em',
      textTransform: 'uppercase',
    },
    caption: {
      fontFamily: fontFam,
      fontWeight: 400,
      fontSize: caption,
      lineHeight: 1.66,
      letterSpacing: '0.03333em',
    },
    overline: {
      fontFamily: fontFam,
      fontWeight: 400,
      fontSize: overline,
      lineHeight: 2.66,
      letterSpacing: '0.08333em',
      textTransform: 'uppercase',
    },
  },
  zIndex: {
    mobileStepper: 1000,
    appBar: 1100,
    drawer: 1200,
    modal: 1300,
    snackbar: 1400,
    tooltip: 1500,
  },
  overrides: {
    MuiButton: {
      contained: {
        boxShadow: 'none',
      },
    },
  },
})

/*

Customizable objects

mixins: {},
breakpoints: {},
direction: 'ltr',
overrides: {},
shadows: {},
shape: {},
spacing: {},
transitions: {},

*/
